/**
 * ProjectActionBar — single dropdown of allowed pipeline transitions.
 *
 * S9-hotfix6 (#1 + #2):
 *   - Replaces stage-direction language ("Promote to ...") with neutral "Move to → X"
 *     so terminal stages (Parked / Graveyard) and lateral moves don't read like promotions.
 *   - Lifted out of QuickEntryTab / OverviewTab and rendered at the ProjectDetail
 *     level so the same toolbar is visible from every tab (Quick Entry, Overview,
 *     Documents, Team, ...).
 */

import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { authFetchJson } from '../../lib/authFetch'
import { ALLOWED_TRANSITIONS, PIPELINE_MAP, type PipelineStage } from '../../lib/pipelineRegistry'

export interface ProjectActionBarProps {
  projectId: number
  currentStage: string
  onTransitioned?: () => void
}

// Heuristic for the visual tone of a target — neutral by default, danger for terminal.
function targetTone(stage: PipelineStage): 'forward' | 'lateral' | 'terminal' {
  if (stage === 'PARKED' || stage === 'GRAVEYARD') return 'terminal'
  // Backward / lateral edges (e.g. PICKED_FOR_REVIEW → INBOX) are tagged in
  // ALLOWED_TRANSITIONS but should not look like a promotion.
  const lateral: PipelineStage[] = ['INBOX', 'LONG_LIST']
  if (lateral.includes(stage)) return 'lateral'
  return 'forward'
}

export function ProjectActionBar({ projectId, currentStage, onTransitioned }: ProjectActionBarProps) {
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const stage = (currentStage || 'INBOX') as PipelineStage
  const allowedTargets = useMemo(() => ALLOWED_TRANSITIONS[stage] || [], [stage])
  const currentLabel = PIPELINE_MAP[stage]?.label ?? stage

  // Click-outside to close
  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const handleTransition = useCallback(
    async (toStage: PipelineStage) => {
      setSubmitting(true)
      setError(null)
      try {
        await authFetchJson(`/api/v2/projects/${projectId}/transitions`, {
          method: 'POST',
          json: { to_stage: toStage, reason: '' },
        })
        setOpen(false)
        onTransitioned?.()
      } catch (err: any) {
        setError(err?.message || 'Failed to move project')
      } finally {
        setSubmitting(false)
      }
    },
    [projectId, onTransitioned]
  )

  if (allowedTargets.length === 0) return null

  return (
    <div className="flex items-center gap-3" ref={wrapperRef}>
      <span className="text-[12px] text-[#8b8ba0] uppercase tracking-wide">Stage:</span>
      <span className="text-[13px] font-medium text-[#e8e8f0]">{currentLabel}</span>

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={submitting}
          className={`
            inline-flex items-center gap-2 px-3 py-1.5
            bg-[#12122a] border border-[#2a2a44]
            rounded-lg
            text-[12px] font-medium text-[#e8e8f0]
            transition-colors
            hover:border-[#3a3a54]
            ${submitting ? 'opacity-50 cursor-wait' : ''}
          `}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {submitting ? 'Moving…' : 'Move to'}
          <span className="text-[#8b8ba0]">▾</span>
        </button>

        {open && (
          <div
            className="
              absolute right-0 top-full mt-1 z-30
              min-w-[200px]
              bg-[#1a1a2e] border border-[#2a2a44]
              rounded-lg shadow-lg
              py-1
            "
            role="listbox"
          >
            {allowedTargets.map((targetStage) => {
              const cfg = PIPELINE_MAP[targetStage]
              if (!cfg) return null
              const tone = targetTone(targetStage)
              return (
                <button
                  key={targetStage}
                  type="button"
                  onClick={() => handleTransition(targetStage)}
                  disabled={submitting}
                  className={`
                    block w-full text-left px-3 py-2
                    text-[12px]
                    transition-colors
                    hover:bg-[#2a2a44]
                    ${tone === 'terminal' ? 'text-[#ff8a8f]' : 'text-[#e8e8f0]'}
                    ${tone === 'lateral' ? 'text-[#a8a8c0]' : ''}
                  `}
                  role="option"
                >
                  → {cfg.label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {error && (
        <span className="text-[12px] text-red-300">{error}</span>
      )}
    </div>
  )
}

export default ProjectActionBar
